import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCurrentUser,
  getCategoryInfo,
  resetCategoryInfo,
  toggleChallengers
} from "./redux/revenueCategoriesSagas";
import LoadingAnimation from "../../../components/loadingAnimation/loadingAnimation";
import { Upload } from "./smartComponents/Upload";
import { Dialog } from "./smartComponents/Dialog";
import { usePopup } from "./hooks/usePopup";
import FilterCategories from "./smartComponents/FilterCategories";
import { RevenueCategoriesMenu } from "./smartComponents/RevenueCategoriesMenu";
import { RevenueCategoryRow } from "./smartComponents/ServicesRow";
import { WindowVirtualList } from "../../../components/WindowVirtualList/WindowVirtualList";
import NoData from "../../../components/noData/noData";
import "./revenueCategories.scss";

const createSection = (items, title, category) => {
  if (!items?.length) return [];
  return [{ type: "header", title, category }, ...items.map((item) => ({ ...item, category }))];
};

function prepareRevenueRows(products, services, searchValue) {
  const filterByName = (items, query) =>
    items.filter((item) => item.name.toLowerCase().includes(query.toLowerCase()));

  const filteredServices = filterByName(services || [], searchValue);
  const filteredProducts = filterByName(products || [], searchValue);

  return [
    ...createSection(filteredServices, "Services", "services"),
    ...createSection(filteredProducts, "Products", "products")
  ];
}

export function RevenueCategoriesModule({ params }) {
  const dispatch = useDispatch();
  const { popup, openPopup, closePopup } = usePopup();

  const isLoading = useSelector((state) => state.revenueCategories.isLoading);
  const services = useSelector((state) => state.revenueCategories.revenueCategories.service);
  const products = useSelector((state) => state.revenueCategories.revenueCategories.product);
  const { canEnableChallengers, canToggleTags } = useSelector(
    (state) => state.revenueCategories.actions
  );
  const [selectedCategory, setSelectedCategory] = useState(null);
  //for opening collapsible element
  const [referralId, setReferralId] = useState(null);
  const [showAllItems, setShowAllItems] = useState(false);

  const reset = () => {
    setReferralId(null);
    setShowAllItems(false);
    dispatch(resetCategoryInfo());
  };

  const rawItems = useMemo(() => ({ services, products }), [services, products]);

  const handleCategory = (id) => {
    if (id === referralId) {
      reset();
    } else {
      setShowAllItems(false);
      setReferralId(id);
      dispatch(getCategoryInfo({ id }));
    }
  };

  const handlePopup = (item, type) => {
    setSelectedCategory(item);
    openPopup(type);
  };

  const onChallengersToggleClick = () => {
    dispatch(
      toggleChallengers({
        rcoId: selectedCategory.rcoId,
        challenger: selectedCategory.challenger
      })
    );
  };

  useEffect(() => {
    dispatch(fetchCurrentUser(params));
  }, []);

  if (isLoading)
    return (
      <div className="light">
        <LoadingAnimation />
      </div>
    );

  const onShowAllClick = (e, isOpened) => {
    e.stopPropagation();
    if (isOpened) {
      setShowAllItems(!showAllItems);
    }
  };

  return (
    <div id="revenue-categories">
      <div className="revenue-categories">
        <div className="revenue-categories__top">
          {/* Add Menu */}
          <div className="revenue-categories__menu">
            <RevenueCategoriesMenu openPopup={openPopup} />
          </div>

          <FilterCategories reset={reset} />
        </div>

        <div className="revenue-categories__content">
          {!products && !services ? (
            <NoData />
          ) : (
            <WindowVirtualList
              rawItems={rawItems}
              processItemsFn={({ products, services }, searchValue) =>
                prepareRevenueRows(products, services, searchValue)
              }
              extraHeight={200}
              renderRow={({ item, measure }) => {
                const props =
                  item.category === "services"
                    ? {
                        onShowAllClick,
                        handleCategory,
                        canEnableChallengers,
                        handlePopup,
                        referralId,
                        canToggleTags,
                        showAllItems,
                        setShowAllItems
                      }
                    : { handleCategory, referralId };

                return <RevenueCategoryRow item={item} measure={measure} {...props} />;
              }}
              getKey={(item, index) => `${item.category}-${item.id || index}`}
            />
          )}
        </div>
        {popup.challengers && (
          <Dialog primaryBtnHandler={onChallengersToggleClick} onClose={closePopup("challengers")}>
            Are you sure to toggle challengers options for
            <br />
            <span className="text-bold">{selectedCategory.name}</span>
          </Dialog>
        )}

        {popup.upload && <Upload onClose={closePopup("upload")} />}
      </div>
    </div>
  );
}
